import { mockResponse, request, isMockMode } from './client.js';

// 詢問單 / 登記 API。後端在 feat/virtual-inquiry。
// 後端跑起來後在 .env 設 REACT_APP_USE_MOCK_INQUIRIES=false 即接真實後端。
//
// InquiryDto：{ id, refNo, userId, productId, productName, payMethod, status, statusLabel,
//               estimatedTotal, createdAt, items:[{id, variantName, cardName, account, note, amount}] }
// refNo = 對客人顯示的業務編號（YYYYMMDDXXXX，隨機尾碼不洩漏總筆數）。

const STATUS_LABEL = {
  SUBMITTED: '已送出', PROCESSING: '處理中', PAID: '已收錢（未結案）', DONE: '已完成', CLOSED: '已結案', CANCELLED: '已取消',
};

let _inquiries = [];
let _nextId = 1;
let _nextItemId = 1;

const clone = (x) => JSON.parse(JSON.stringify(x));

// mock 用：產生 YYYYMMDDXXXX 業務編號
const mockRefNo = () =>
  new Date().toISOString().slice(0, 10).replace(/-/g, '') + Math.floor(Math.random() * 9999).toString().padStart(4, '0');

// ── 前台 ──────────────────────────────────────────────

export function submitInquiry(payload) {
  if (!isMockMode('inquiries')) return request('/api/inquiries', { method: 'POST', body: payload });
  return mockResponse(() => {
    const items = (payload.items || []).map(it => ({ id: _nextItemId++, ...it, amount: Number(it.amount) || 0 }));
    const total = items.reduce((s, it) => s + it.amount, 0);
    const inq = {
      id: _nextId++,
      refNo: mockRefNo(),
      userId: 'mock-user',
      productId: payload.productId,
      productName: payload.productName,
      payMethod: payload.payMethod,
      status: 'SUBMITTED',
      statusLabel: STATUS_LABEL.SUBMITTED,
      estimatedTotal: total,
      pointsRedeemed: Math.min(Number(payload.pointsToRedeem) || 0, total),
      createdAt: new Date().toISOString(),
      items,
    };
    _inquiries.unshift(inq);
    return clone(inq);
  });
}

export function getMyInquiries() {
  if (!isMockMode('inquiries')) return request('/api/inquiries/me');
  return mockResponse(() => _inquiries.map(clone));
}

// 買家確認結案（僅限自己的、且狀態為已完成）
export function confirmInquiry(id) {
  if (!isMockMode('inquiries')) return request(`/api/inquiries/${id}/confirm`, { method: 'PATCH' });
  return mockResponse(() => {
    const inq = _inquiries.find(i => i.id === id);
    if (!inq) throw new Error('找不到該登記單');
    if (inq.status !== 'DONE') throw new Error('只有「已完成」的登記才能確認結案');
    inq.status = 'CLOSED';
    inq.statusLabel = STATUS_LABEL.CLOSED;
    return clone(inq);
  });
}

// ── 後台 ──────────────────────────────────────────────

export function adminGetInquiries({ status, productId, q, sort, page = 1, limit = 10 } = {}) {
  if (!isMockMode('inquiries')) {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    if (productId) qs.set('productId', productId);
    if (q) qs.set('q', q);
    if (sort) qs.set('sort', sort);
    qs.set('page', page);
    qs.set('limit', limit);
    return request(`/api/admin/inquiries?${qs.toString()}`);
  }
  return mockResponse(() => {
    const [sf, sd] = (sort || '').split(',');
    let filtered = status ? _inquiries.filter(i => i.status === status) : [..._inquiries];
    if (productId) filtered = filtered.filter(i => i.productId === productId);
    if (q) {
      const kw = String(q).toLowerCase();
      filtered = filtered.filter(i => (String(i.refNo || '') + i.productName).toLowerCase().includes(kw));
    }
    if (sf) {
      filtered.sort((a, b) => {
        const r = String(a[sf] ?? '').localeCompare(String(b[sf] ?? ''), undefined, { numeric: true });
        return sd === 'desc' ? -r : r;
      });
    }
    const start = (page - 1) * limit;
    const items = filtered.slice(start, start + limit).map(clone);
    return { items, total: filtered.length, page, limit, totalPages: Math.ceil(filtered.length / limit) };
  });
}

export function adminGetInquiryProductOptions() {
  if (!isMockMode('inquiries')) return request('/api/admin/inquiries/product-options');
  return mockResponse(() => {
    const incomplete = new Set(['SUBMITTED', 'PROCESSING', 'PAID']);
    const byProduct = new Map();
    _inquiries.filter(i => incomplete.has(i.status) && i.productId).forEach(i => {
      const current = byProduct.get(i.productId) || {
        productId: i.productId,
        productName: i.productName,
        incompleteCount: 0,
      };
      current.incompleteCount += 1;
      byProduct.set(i.productId, current);
    });
    return [...byProduct.values()].sort((a, b) =>
      b.incompleteCount - a.incompleteCount || a.productName.localeCompare(b.productName));
  });
}

export function adminUpdateInquiryStatus(id, status) {
  if (!isMockMode('inquiries')) {
    return request(`/api/admin/inquiries/${id}/status`, { method: 'PATCH', body: { status } });
  }
  return mockResponse(() => {
    const inq = _inquiries.find(i => i.id === id);
    if (inq) { inq.status = status; inq.statusLabel = STATUS_LABEL[status] || status; }
    return inq ? clone(inq) : null;
  });
}

// 後台改金額：逐列更新（items: [{ id, amount }]），總額由後端重算。
export function adminUpdateInquiryAmount(id, items) {
  if (!isMockMode('inquiries')) {
    return request(`/api/admin/inquiries/${id}/amount`, { method: 'PATCH', body: { items } });
  }
  return mockResponse(() => {
    const inq = _inquiries.find(i => i.id === id);
    if (!inq) throw new Error('找不到該登記單');
    const amountById = {};
    items.forEach(it => { amountById[it.id] = Number(it.amount) || 0; });
    inq.items = inq.items.map(it => (amountById[it.id] != null ? { ...it, amount: amountById[it.id] } : it));
    inq.estimatedTotal = inq.items.reduce((s, it) => s + it.amount, 0);
    return clone(inq);
  });
}

export function adminDeleteInquiry(id) {
  if (!isMockMode('inquiries')) return request(`/api/admin/inquiries/${id}`, { method: 'DELETE' });
  return mockResponse(() => {
    _inquiries = _inquiries.filter(i => i.id !== id);
    return null;
  });
}
